import type { BillingInvoice } from "@/server-types";

export const BILLING_TABS = ["narocnina", "racuni", "sms"] as const;
export type BillingTab = (typeof BILLING_TABS)[number];

export type UnpaidAlertKind = "cardFailed" | "overdue" | "open";

export type UnpaidAlert = {
  kind: UnpaidAlertKind;
  tone: "danger" | "warning";
  unpaid: BillingInvoice[];
};

/** The block at the top of the billing page, or null when nothing is owed.
 * A declined card outranks an overdue invoice, which outranks one still open. */
export function deriveUnpaidAlert(
  invoices: BillingInvoice[],
  cardFailureCount: number,
): UnpaidAlert | null {
  const unpaid = invoices.filter(
    (invoice) => invoice.status === "open" || invoice.status === "overdue",
  );
  if (unpaid.length === 0 && cardFailureCount === 0) return null;

  const kind: UnpaidAlertKind =
    cardFailureCount > 0 || unpaid.some((invoice) => invoice.cardPaymentFailed)
      ? "cardFailed"
      : unpaid.some((invoice) => invoice.status === "overdue")
        ? "overdue"
        : "open";

  return { kind, tone: kind === "open" ? "warning" : "danger", unpaid };
}

export type BalanceKind = "owed" | "credit" | "settled";

/** Ledger balance sign: positive is owed by the client, negative is credit. */
export function balanceKind(balanceCents: number): BalanceKind {
  if (balanceCents > 0) return "owed";
  if (balanceCents < 0) return "credit";
  return "settled";
}

export type SmsUsage = {
  remaining: number;
  percentUsed: number;
};

export function smsUsage(sent: number, total: number): SmsUsage {
  if (total <= 0) return { remaining: 0, percentUsed: 0 };

  return {
    remaining: Math.max(0, total - sent),
    percentUsed: Math.min(100, Math.max(0, Math.round((sent / total) * 100))),
  };
}

export function isBillingPagePath(pathname: string): boolean {
  return (
    pathname === "/dashboard/billing" ||
    pathname.startsWith("/dashboard/billing/")
  );
}
